// Iter 82.13 / Phase E3.2 Sprint 3 — Webhook Events Ledger (D13).
//
// Operator-facing read view over the immutable payroll_webhook_events
// collection. Supports filter by processing_status / event_type /
// signature_valid + pagination. Per-row JSON drawer for forensics.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Lightning, ShieldCheck, ShieldWarning, ArrowsClockwise, MagnifyingGlass,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../../components/ui/sheet";
import { toast } from "sonner";

const STATUS_TONE = {
    applied: "bg-emerald-100 text-emerald-800",
    ignored_duplicate: "bg-slate-100 text-slate-700",
    ignored_unknown_event: "bg-amber-100 text-amber-800",
    ignored_unknown_payslip: "bg-amber-100 text-amber-800",
    ignored_opens_disabled: "bg-slate-100 text-slate-600",
    signature_failed: "bg-rose-100 text-rose-800",
    anti_replay_rejected: "bg-rose-100 text-rose-800",
    invalid_payload: "bg-rose-100 text-rose-800",
};

const EVENT_TONE = {
    "email.sent": "bg-blue-50 text-blue-800",
    "email.delivered": "bg-emerald-50 text-emerald-800",
    "email.bounced": "bg-rose-50 text-rose-800",
    "email.complained": "bg-rose-50 text-rose-800",
    "email.delivery_delayed": "bg-amber-50 text-amber-800",
    "email.opened": "bg-slate-50 text-slate-600",
    "email.clicked": "bg-slate-50 text-slate-600",
    "email.failed": "bg-rose-50 text-rose-800",
};

export default function PayrollWebhookEvents() {
    const [stats, setStats] = useState(null);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(50);
    const [filterStatus, setFilterStatus] = useState("");
    const [filterEvent, setFilterEvent] = useState("");
    const [filterValid, setFilterValid] = useState("");  // "" | "true" | "false"
    const [loading, setLoading] = useState(true);
    const [drawer, setDrawer] = useState({ open: false, item: null });
    const [busyRecover, setBusyRecover] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, page_size: pageSize };
            if (filterStatus) params.processing_status = filterStatus;
            if (filterEvent) params.event_type = filterEvent;
            if (filterValid) params.signature_valid = filterValid;
            const [s, l] = await Promise.all([
                api.get("/payroll/webhooks/stats"),
                api.get("/payroll/webhooks/list", { params }),
            ]);
            setStats(s.data);
            setItems(l.data.items || []);
            setTotal(l.data.total || 0);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load webhook ledger"));
        } finally { setLoading(false); }
    }, [page, pageSize, filterStatus, filterEvent, filterValid]);
    useEffect(() => { reload(); }, [reload]);

    const openDrawer = async (id) => {
        try {
            const r = await api.get(`/payroll/webhooks/${id}`);
            setDrawer({ open: true, item: r.data });
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load event"));
        }
    };

    if (loading && !stats) return <div className="p-6 text-slate-500" data-testid="webhook-loading">Loading webhook ledger…</div>;

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    return (
        <div className="space-y-4" data-testid="payroll-webhook-events-page">
            <PageHeader
                eyebrow="Payroll Operations · Delivery Intelligence"
                title="Webhook Events Ledger"
                description="D13 immutable audit ledger for Resend delivery webhooks. Signature failures, anti-replay rejections, idempotency duplicates — every event persisted with forensic detail."
                actions={(
                    <Button variant="outline" size="sm" onClick={reload} data-testid="webhook-refresh-btn">
                        <ArrowsClockwise size={14} className="mr-1" /> Refresh
                    </Button>
                )}
            />

            {/* Status strip */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="webhook-stats">
                    <div className="border rounded-lg bg-white p-4" data-testid="webhook-stat-total">
                        <div className="text-[11px] uppercase text-slate-500">Total Events</div>
                        <div className="text-2xl font-mono font-semibold mt-0.5">{stats.total_events}</div>
                    </div>
                    <div className="border rounded-lg bg-white p-4" data-testid="webhook-stat-invalid">
                        <div className="text-[11px] uppercase text-slate-500">Signature Invalid</div>
                        <div className={`text-2xl font-mono font-semibold mt-0.5 ${stats.signature_invalid_count > 0 ? "text-rose-700" : "text-slate-400"}`}>
                            {stats.signature_invalid_count}
                        </div>
                    </div>
                    <div className="border rounded-lg bg-white p-4" data-testid="webhook-stat-secret">
                        <div className="text-[11px] uppercase text-slate-500">Webhook Secret</div>
                        <div className="mt-1">
                            {stats.webhook_secret_configured ? (
                                <Badge className="bg-emerald-100 text-emerald-800"><ShieldCheck size={12} className="inline mr-1" /> Configured</Badge>
                            ) : (
                                <Badge className="bg-rose-100 text-rose-800"><ShieldWarning size={12} className="inline mr-1" /> Not Configured</Badge>
                            )}
                        </div>
                    </div>
                    <div className="border rounded-lg bg-white p-4" data-testid="webhook-stat-opens">
                        <div className="text-[11px] uppercase text-slate-500">Opens / Clicks Tracking</div>
                        <div className="mt-1">
                            <Badge className={stats.opens_tracking_enabled ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}>
                                {stats.opens_tracking_enabled ? "Enabled" : "Disabled (D7 default)"}
                            </Badge>
                        </div>
                    </div>
                </div>
            )}

            {/* Counters by status + event */}
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="border rounded-lg bg-white p-4" data-testid="webhook-by-status">
                        <div className="text-xs uppercase text-slate-500 tracking-wide mb-2">By Processing Status</div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                            {Object.entries(stats.by_processing_status || {}).map(([k, v]) => (
                                <button
                                    key={k}
                                    type="button"
                                    onClick={() => { setFilterStatus(k); setPage(1); }}
                                    className="border rounded px-2 py-1 text-left hover:border-slate-400"
                                    data-testid={`webhook-by-status-${k}`}>
                                    <div className="flex justify-between">
                                        <span className="truncate">{k}</span>
                                        <span className="font-mono font-semibold">{v}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="border rounded-lg bg-white p-4" data-testid="webhook-by-event">
                        <div className="text-xs uppercase text-slate-500 tracking-wide mb-2">By Event Type</div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                            {Object.entries(stats.by_event_type || {}).map(([k, v]) => (
                                <button
                                    key={k}
                                    type="button"
                                    onClick={() => { setFilterEvent(k); setPage(1); }}
                                    className="border rounded px-2 py-1 text-left hover:border-slate-400"
                                    data-testid={`webhook-by-event-${k}`}>
                                    <div className="flex justify-between">
                                        <span className="truncate">{k}</span>
                                        <span className="font-mono font-semibold">{v}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Filters + ledger table */}
            <div className="border rounded-lg bg-white" data-testid="webhook-ledger-table">
                <div className="flex items-center gap-2 p-3 border-b">
                    <div className="text-xs uppercase text-slate-500 tracking-wide">Ledger</div>
                    <span className="text-xs text-slate-400">{total} events</span>
                    <span className="flex-1" />
                    {(filterStatus || filterEvent || filterValid) && (
                        <Button variant="outline" size="sm"
                            onClick={() => { setFilterStatus(""); setFilterEvent(""); setFilterValid(""); setPage(1); }}
                            data-testid="webhook-clear-filters">
                            Clear filters
                        </Button>
                    )}
                </div>
                <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500">
                        <tr>
                            <th className="px-3 py-2 text-left">Received</th>
                            <th className="px-3 py-2 text-left">Event</th>
                            <th className="px-3 py-2 text-left">Processing</th>
                            <th className="px-3 py-2 text-left">Signature</th>
                            <th className="px-3 py-2 text-left">Payslip</th>
                            <th className="px-3 py-2 text-left">Failure Reason</th>
                            <th className="px-3 py-2 text-left">Retries</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {items.length === 0 ? (
                            <tr><td colSpan={7} className="text-center text-slate-400 py-6" data-testid="webhook-empty">
                                No webhook events match the current filters.
                            </td></tr>
                        ) : items.map((row) => (
                            <tr key={row.id} className="hover:bg-slate-50 cursor-pointer"
                                onClick={() => openDrawer(row.id)}
                                data-testid={`webhook-row-${row.id}`}>
                                <td className="px-3 py-2 font-mono text-[11px]">{row.received_at?.slice(0, 19).replace("T", " ")}</td>
                                <td className="px-3 py-2">
                                    <Badge className={`${EVENT_TONE[row.event_type] || "bg-slate-100"} text-[10px]`}>
                                        {row.event_type}
                                    </Badge>
                                </td>
                                <td className="px-3 py-2">
                                    <Badge className={`${STATUS_TONE[row.processing_status] || "bg-slate-100"} text-[10px]`}>
                                        {row.processing_status}
                                    </Badge>
                                </td>
                                <td className="px-3 py-2">
                                    {row.signature_valid
                                        ? <ShieldCheck size={14} className="text-emerald-600" />
                                        : <ShieldWarning size={14} className="text-rose-600" />}
                                </td>
                                <td className="px-3 py-2 font-mono text-[10px]">{row.payslip_id || "—"}</td>
                                <td className="px-3 py-2 text-slate-600">{row.failure_reason || "—"}</td>
                                <td className="px-3 py-2 font-mono">{row.retry_count || 0}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="flex items-center justify-between p-3 border-t text-xs">
                    <div className="text-slate-400">Page {page} of {totalPages}</div>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            data-testid="webhook-prev-page">Prev</Button>
                        <Button variant="outline" size="sm" disabled={page >= totalPages}
                            onClick={() => setPage((p) => p + 1)}
                            data-testid="webhook-next-page">Next</Button>
                    </div>
                </div>
            </div>

            {/* Forensics drawer */}
            <Sheet open={drawer.open} onOpenChange={(open) => setDrawer({ open, item: open ? drawer.item : null })}>
                <SheetContent className="w-[640px] sm:max-w-[640px] overflow-y-auto" data-testid="webhook-drawer">
                    <SheetHeader>
                        <SheetTitle>Webhook Event · {drawer.item?.id}</SheetTitle>
                        <SheetDescription className="text-xs text-slate-500">
                            Immutable D13 ledger row showing signature outcome, processing status, and full raw payload for forensics.
                        </SheetDescription>
                    </SheetHeader>
                    {drawer.item && drawer.item.processing_status === "ignored_unknown_payslip" && (
                        <div className="mt-4 p-3 border border-amber-200 bg-amber-50 rounded space-y-2" data-testid="webhook-recover-banner">
                            <div className="text-xs text-amber-900">
                                This event arrived before the payslip was committed (race condition).
                                If the payslip now exists, you can recover the missed state transition.
                                The original row will NOT be mutated — a new chained row will be appended.
                            </div>
                            <Button
                                size="sm"
                                disabled={busyRecover}
                                onClick={async () => {
                                    setBusyRecover(true);
                                    try {
                                        const r = await api.post(`/payroll/webhooks/${drawer.item.id}/recover`);
                                        toast.success(`Recovered → payslip ${r.data.payslip_id} now ${r.data.to_state || "updated"}`);
                                        setDrawer({ open: false, item: null });
                                        await reload();
                                    } catch (err) {
                                        toast.error(formatApiError(err, "Recovery failed"));
                                    } finally { setBusyRecover(false); }
                                }}
                                className="bg-amber-600 hover:bg-amber-700 text-white"
                                data-testid="webhook-recover-btn"
                            >
                                {busyRecover ? "Recovering…" : "Attempt Recovery"}
                            </Button>
                        </div>
                    )}
                    {drawer.item && (
                        <div className="mt-4 space-y-3 text-xs">
                            <dl className="grid grid-cols-2 gap-2">
                                <div><dt className="text-slate-500 text-[10px] uppercase">Provider Event ID</dt><dd className="font-mono">{drawer.item.provider_event_id || "—"}</dd></div>
                                <div><dt className="text-slate-500 text-[10px] uppercase">Event Type</dt><dd className="font-mono">{drawer.item.event_type}</dd></div>
                                <div><dt className="text-slate-500 text-[10px] uppercase">Received At</dt><dd className="font-mono">{drawer.item.received_at}</dd></div>
                                <div><dt className="text-slate-500 text-[10px] uppercase">Signature Valid</dt><dd className="font-mono">{String(drawer.item.signature_valid)}</dd></div>
                                <div><dt className="text-slate-500 text-[10px] uppercase">Processing Status</dt><dd className="font-mono">{drawer.item.processing_status}</dd></div>
                                <div><dt className="text-slate-500 text-[10px] uppercase">Retry Count</dt><dd className="font-mono">{drawer.item.retry_count}</dd></div>
                                <div><dt className="text-slate-500 text-[10px] uppercase">Failure Reason</dt><dd className="font-mono">{drawer.item.failure_reason || "—"}</dd></div>
                                <div><dt className="text-slate-500 text-[10px] uppercase">Payslip ID</dt><dd className="font-mono">{drawer.item.payslip_id || "—"}</dd></div>
                            </dl>
                            <div>
                                <div className="text-[10px] uppercase text-slate-500 mb-1">Svix Headers</div>
                                <pre className="bg-slate-50 rounded p-2 overflow-x-auto">{JSON.stringify(drawer.item.svix_headers, null, 2)}</pre>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase text-slate-500 mb-1">Raw Payload</div>
                                <pre className="bg-slate-50 rounded p-2 overflow-x-auto max-h-96">{JSON.stringify(drawer.item.payload, null, 2)}</pre>
                            </div>
                        </div>
                    )}
                </SheetContent>
            </Sheet>
        </div>
    );
}
